/**
 * useFeedbackSeen — 문의 답변 "열람 확인" 마지막 시각 (queue #691 · docs/biz-specs/common-showcase.md
 * §UAT-2026-07-14-R691 §R691-2(b))
 *
 * localStorage `eraf.showcase.feedbackSeenAt` 에 ISO-8601 문자열을 저장하는 공유 훅.
 * `_shared/hooks/useScaffoldCart.ts`(§R682) 의 useSyncExternalStore + 모듈-레벨 리스너 집합 +
 * 커스텀 이벤트(동일 탭 즉시 동기화) 패턴을 그대로 답습한다. localStorage 미가용(프라이빗
 * 모드 등)이면 메모리 폴백(세션 한정)으로 전환한다.
 */
import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'eraf.showcase.feedbackSeenAt';
const SEEN_EVENT = 'eraf:showcase-feedback-seen';

const listeners = new Set<() => void>();

let memoryFallback: string | null = null;
let cachedRaw: string | null | undefined; // undefined = 아직 미조회
let cachedSnapshot: string | null = null;

function parseStored(raw: string | null): string | null {
  if (!raw) return null;
  const ms = Date.parse(raw);
  if (Number.isNaN(ms)) return null; // 손상된 값 — 미열람(epoch 0) 취급으로 폴백
  return raw;
}

function readSeen(): string | null {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return memoryFallback;
  }
  if (raw === cachedRaw) return cachedSnapshot;
  cachedRaw = raw;
  cachedSnapshot = parseStored(raw);
  return cachedSnapshot;
}

function writeSeen(iso: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, iso);
    cachedRaw = iso;
    cachedSnapshot = iso;
  } catch {
    memoryFallback = iso;
    cachedSnapshot = iso;
  }
  listeners.forEach((listener) => listener());
  try {
    window.dispatchEvent(new Event(SEEN_EVENT));
  } catch {
    // 이벤트 디스패치 실패는 무해 — 동일 탭 listener 는 위에서 이미 통지됨.
  }
}

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  window.addEventListener('storage', onStoreChange);
  window.addEventListener(SEEN_EVENT, onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
    window.removeEventListener('storage', onStoreChange);
    window.removeEventListener(SEEN_EVENT, onStoreChange);
  };
}

function getServerSnapshot(): string | null {
  return null;
}

export interface UseFeedbackSeenResult {
  /** 마지막 열람 시각(ISO) · 미열람(최초 사용자)이면 null */
  lastSeenAt: string | null;
  /** epoch(0) 취급 포함 — 미열람이면 0(§R691-2(a): 알림 누락보다 1회 과다 알림이 안전) */
  lastSeenAtMs: number;
  /** 지금 시각으로 열람 확인 처리(배지 소거) */
  markSeen: () => void;
}

export function useFeedbackSeen(): UseFeedbackSeenResult {
  const lastSeenAt = useSyncExternalStore(subscribe, readSeen, getServerSnapshot);

  const markSeen = useCallback(() => {
    writeSeen(new Date().toISOString());
  }, []);

  return {
    lastSeenAt,
    lastSeenAtMs: lastSeenAt ? Date.parse(lastSeenAt) : 0,
    markSeen,
  };
}
